import { createGenericStore } from '@/stores/utils/genericStore.js'
import { useAuthStore } from '@/stores/auth.js'
import axios from 'axios'
import { numericId, sortMessages, unwrapResource } from './chatStoreUtils.js'

const deliveryStatusRank = {
  failed: -1,
  pending: 0,
  sent: 1,
  received: 2
}

const messageStoreOptions = {
  state: () => ({
    messagesByConversationId: {},
    messagePaginationByConversationId: {},
    isLoadingMessages: false,
    isSendingMessage: false,
    optimisticMessageSequence: 0
  }),

  getters: {
    getMessagesByConversationId(state) {
      return (conversationId) => state.messagesByConversationId[conversationId] || []
    },
    getMessagePaginationByConversationId(state) {
      return (conversationId) => state.messagePaginationByConversationId[conversationId] || null
    }
  },

  actions: {
    clearMessages(conversationId) {
      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: []
      }
      this.messagePaginationByConversationId = {
        ...this.messagePaginationByConversationId,
        [conversationId]: null
      }
    },

    patchMessageState(conversationId, message) {
      if (!message?.id) {
        return
      }

      const messages = [...(this.messagesByConversationId[conversationId] || [])]
      const messageIndex = messages.findIndex((item) => numericId(item.id) === numericId(message.id))
      const existingMessage = messageIndex > -1 ? messages[messageIndex] : null
      const patchedMessage = {
        ...message,
        delivery_status: this.resolveDeliveryStatus(message.delivery_status, existingMessage?.delivery_status),
        is_optimistic: message.is_optimistic ?? existingMessage?.is_optimistic ?? false
      }

      if (messageIndex > -1) {
        messages.splice(messageIndex, 1, patchedMessage)
      } else {
        messages.push(patchedMessage)
      }

      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: sortMessages(messages)
      }
    },

    replaceOptimisticMessageState(conversationId, optimisticMessageId, message) {
      const messages = (this.messagesByConversationId[conversationId] || [])
        .filter((item) => numericId(item.id) !== numericId(optimisticMessageId))

      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: messages
      }

      this.patchMessageState(conversationId, {
        ...message,
        is_optimistic: false
      })
    },

    markOptimisticMessageFailed(conversationId, optimisticMessageId) {
      const messages = [...(this.messagesByConversationId[conversationId] || [])]
      const messageIndex = messages.findIndex((item) => numericId(item.id) === numericId(optimisticMessageId))

      if (messageIndex === -1) {
        return
      }

      messages.splice(messageIndex, 1, {
        ...messages[messageIndex],
        delivery_status: 'failed',
        is_optimistic: true
      })

      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: sortMessages(messages)
      }
    },

    optimisticMessageFromPayload(conversationId, payload, options = {}) {
      const authStore = useAuthStore()
      const sender = authStore.user ? {
        id: authStore.user.id,
        name: authStore.user.name
      } : null
      const createdAt = new Date().toISOString()

      this.optimisticMessageSequence += 1

      return {
        id: -((Date.now() * 1000) + this.optimisticMessageSequence),
        conversation_id: numericId(conversationId),
        sender_user_id: sender?.id ?? null,
        type: payload.type ?? 'text',
        body: payload.body ?? null,
        payload: payload.payload ?? null,
        reply_to_message_id: payload.reply_to_message_id ?? null,
        edited_at: null,
        created_at: createdAt,
        updated_at: createdAt,
        sender,
        reply_to_message: null,
        reactions: [],
        uploads: options.optimisticUploads ?? [],
        delivery_status: 'pending',
        is_optimistic: true
      }
    },

    resolveDeliveryStatus(incomingStatus, existingStatus = null) {
      if (!existingStatus) {
        return incomingStatus
      }

      if (!incomingStatus) {
        return existingStatus
      }

      return deliveryStatusRank[incomingStatus] >= deliveryStatusRank[existingStatus]
        ? incomingStatus
        : existingStatus
    },

    removeMessageFromState(conversationId, messageId) {
      const messages = this.messagesByConversationId[conversationId] || []

      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: messages.filter((message) => numericId(message.id) !== numericId(messageId))
      }
    },

    patchMessageReactionState(conversationId, messageId, reaction, action = 'added') {
      const messages = [...(this.messagesByConversationId[conversationId] || [])]
      const messageIndex = messages.findIndex((message) => numericId(message.id) === numericId(messageId))

      if (messageIndex === -1 || !reaction) {
        return
      }

      const message = messages[messageIndex]
      const reactions = Array.isArray(message.reactions) ? [...message.reactions] : []
      const reactionIndex = reactions.findIndex((item) => numericId(item.id) === numericId(reaction.id))

      if (action === 'removed') {
        messages.splice(messageIndex, 1, {
          ...message,
          reactions: reactions.filter((item) => numericId(item.id) !== numericId(reaction.id))
        })
      } else if (reactionIndex > -1) {
        reactions.splice(reactionIndex, 1, reaction)
        messages.splice(messageIndex, 1, { ...message, reactions })
      } else {
        messages.splice(messageIndex, 1, { ...message, reactions: [...reactions, reaction] })
      }

      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: sortMessages(messages)
      }
    },

    removeMessageReactionByNameState(conversationId, messageId, reactionName) {
      const messages = [...(this.messagesByConversationId[conversationId] || [])]
      const messageIndex = messages.findIndex((message) => numericId(message.id) === numericId(messageId))

      if (messageIndex === -1) {
        return
      }

      const message = messages[messageIndex]
      const reactions = Array.isArray(message.reactions) ? [...message.reactions] : []

      messages.splice(messageIndex, 1, {
        ...message,
        reactions: reactions.filter((reaction) => reaction.reaction !== reactionName)
      })

      this.messagesByConversationId = {
        ...this.messagesByConversationId,
        [conversationId]: sortMessages(messages)
      }
    },

    async fetchMessages(conversationId, params = {}) {
      this.isLoadingMessages = true
      this.error = null

      const requestParams = {
        per_page: 25,
        order: 'asc',
        clear: true,
        ...params
      }

      try {
        const response = await axios.get(`/api/v1/conversations/${conversationId}/messages`, { params: requestParams })
        const messages = response.data?.data ?? []

        if (requestParams.clear) {
          this.messagesByConversationId = {
            ...this.messagesByConversationId,
            [conversationId]: sortMessages([...messages])
          }
        } else {
          for (const message of messages) {
            this.patchMessageState(conversationId, message)
          }
        }

        this.messagePaginationByConversationId = {
          ...this.messagePaginationByConversationId,
          [conversationId]: response.data?.meta ?? null
        }

        return response.data
      } catch (error) {
        this.error = error
        throw error
      } finally {
        this.isLoadingMessages = false
      }
    },

    async fetchOlderMessages(conversationId, params = {}) {
      const messages = this.messagesByConversationId[conversationId] || []
      const firstMessage = messages[0]

      return this.fetchMessages(conversationId, {
        before_id: firstMessage?.id,
        order: 'desc',
        clear: false,
        ...params
      })
    },

    async sendMessage(conversationId, payload, options = {}) {
      this.isSendingMessage = true
      this.error = null
      const optimisticMessage = this.optimisticMessageFromPayload(conversationId, payload, options)

      this.patchMessageState(conversationId, optimisticMessage)

      try {
        const response = await axios.post(`/api/v1/conversations/${conversationId}/messages`, payload)
        const message = unwrapResource(response)
        const sentMessage = {
          ...message,
          delivery_status: 'sent'
        }

        this.replaceOptimisticMessageState(conversationId, optimisticMessage.id, sentMessage)
        return sentMessage
      } catch (error) {
        this.error = error
        this.markOptimisticMessageFailed(conversationId, optimisticMessage.id)
        throw error
      } finally {
        this.isSendingMessage = false
      }
    },

    async updateMessage(conversationId, messageId, payload) {
      this.error = null

      try {
        const response = await axios.patch(`/api/v1/conversations/${conversationId}/messages/${messageId}`, payload)
        const message = unwrapResource(response)
        this.patchMessageState(conversationId, message)
        return message
      } catch (error) {
        this.error = error
        throw error
      }
    },

    async deleteMessage(conversationId, messageId) {
      this.error = null

      try {
        await axios.delete(`/api/v1/conversations/${conversationId}/messages/${messageId}`)
        this.removeMessageFromState(conversationId, messageId)
      } catch (error) {
        this.error = error
        throw error
      }
    }
  }
}

export const useMessageStore = createGenericStore(
  'chat-messages',
  '/api/v1/conversations',
  messageStoreOptions
)
